import React from 'react';
import { Loader2 } from 'lucide-react';
import { VR_TRIGGER_OPTIONS, EVENT_MODES } from './VrMindMapConstants';

const EventModal = ({
  show,
  onClose,
  eventMode,
  eventForm,
  onFormChange,
  onTriggerSelection,
  onEventSubmit,
  onEventDelete,
  saving,
  currentEvent,
}) => {
  if (!show) return null;

  const isAddTriggerMode = eventMode === EVENT_MODES.ADD_TRIGGER;
  const isAddActionMode = eventMode === EVENT_MODES.ADD_ACTION;
  const isEditMode = eventMode === EVENT_MODES.EDIT;

  const handleSubmit = () => {
    if (isAddTriggerMode) {
      if (!eventForm.vr_trigger) {
        alert('Please select a trigger');
        return;
      }
      onTriggerSelection(eventForm.vr_trigger);
    } else {
      onEventSubmit();
    }
  };

  const getTitle = () => {
    if (isAddTriggerMode) return 'Select VR Trigger';
    if (isAddActionMode) return 'Add Action to Trigger';
    return 'Edit VR Event';
  };

  const getButtonText = () => {
    if (saving) return 'Saving...';
    if (isAddTriggerMode) return 'Add Trigger';
    if (isAddActionMode) return 'Add Action';
    return 'Update Event';
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#1A1B1E] border border-[#37383F] rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4">{getTitle()}</h3>

        <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
          {(isAddTriggerMode || isEditMode) && (
            <div>
              <label
                htmlFor="vr_trigger"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                VR Trigger {isAddTriggerMode ? '*' : '(optional)'}
              </label>
              <select
                id="vr_trigger"
                value={eventForm.vr_trigger}
                onChange={(e) =>
                  onFormChange({ ...eventForm, vr_trigger: e.target.value })
                }
                className="w-full px-3 py-2 bg-[#131418] border border-[#37383F] rounded-lg focus:ring-2 focus:ring-[#EE2B2A] focus:border-transparent text-white"
              >
                <option value="">Select a trigger</option>
                {VR_TRIGGER_OPTIONS.map((option) => (
                  <option key={option} value={option} className="bg-[#131418]">
                    {option}
                  </option>
                ))}
              </select>
              {isAddTriggerMode && (
                <p className="text-xs text-gray-400 mt-1">
                  After selecting a trigger, click on the trigger node in the
                  graph to add an action.
                </p>
              )}
            </div>
          )}

          {isAddActionMode && (
            <div>
              <label
                htmlFor="action"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Action {isAddActionMode ? '*' : ''}
              </label>
              {isAddActionMode && eventForm.vr_trigger && (
                <p className="text-xs text-gray-400 mb-2">
                  Trigger:{' '}
                  <span className="font-medium text-gray-300">
                    {eventForm.vr_trigger}
                  </span>
                </p>
              )}
              <input
                id="action"
                type="text"
                value={eventForm.action}
                onChange={(e) =>
                  onFormChange({ ...eventForm, action: e.target.value })
                }
                placeholder="e.g., play_animation, play_sound, highlight"
                className="w-full px-3 py-2 bg-[#131418] border border-[#37383F] rounded-lg focus:ring-2 focus:ring-[#EE2B2A] focus:border-transparent text-white placeholder-gray-500"
              />
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <div>
            {isEditMode && (
              <button
                onClick={onEventDelete}
                disabled={saving}
                className="px-4 py-2 text-white bg-[#EE2B2A] rounded-lg hover:bg-[#d02524] transition disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Delete Event
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-300 bg-[#2B2C2F] border border-[#37383F] rounded-lg hover:bg-[#37383F] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-white bg-[#EE2B2A] rounded-lg hover:bg-[#d02524] transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {getButtonText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
